import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import type { Subasta } from '../componentes/Compra';
import { ResultadoSemaforo, type Semaforo } from '../componentes/Importacion';
import { fechaCorta, fechaHoraLocal, mxn } from '../formato';
import { nombreVehiculo } from './Tablero';
import type { Vehiculo } from './tipos';

interface Puja {
  semaforo: Semaforo;
  pujaMaximaUsd: string | null;
  pujaSugeridaUsd?: string | null;
  tipoImportacion?: string | null;
  techoMxn?: string;
  margenObjetivoMxn?: string;
  cotizacionId?: string | null;
  limiteMxn?: string;
  motivo?: string;
  desglose: {
    cargos: { concepto: string; cantidad: string }[];
    impuestos: { totalMxn: string } | null;
    importacionMxn: string;
    totalMxn: string;
    aproximado?: boolean;
    tramitesUsd?: string;
  } | null;
}
interface Compra {
  id: string;
  vehiculoId: string;
  vin: string;
  subasta: string;
  numeroLote: string;
  fechaCompra: string;
  moneda: string;
  precioMartillo: string;
}

const hoy = () => fechaHoraLocal(new Date()).slice(0, 10);
const usd = (c: string) => `USD ${Number(c).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

/** Subastas (módulo Auctions): calculadora de puja máxima y compras registradas. */
export function Subastas() {
  const api = useApi();
  const { datos: subastas } = useConsulta<Subasta[]>('/sourcing/subastas');
  const { datos: vehiculos } = useConsulta<Vehiculo[]>('/inventory/vehiculos');
  const { datos: compras } = useConsulta<Compra[]>('/sourcing/compras');
  const [fix, setFix] = useState<string>('');
  const [vehiculo, setVehiculo] = useState<Vehiculo | null>(null);
  const [puja, setPuja] = useState<Puja | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [calculando, setCalculando] = useState(false);

  useEffect(() => {
    api<{ valor: string }>('GET', `/finance/tipos-cambio/${hoy()}`)
      .then((t) => setFix(t.valor))
      .catch(() => setFix(''));
  }, [api]);

  const calcular = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const num = (k: string) => String(f.get(k) ?? '').trim() || '0';
    setCalculando(true);
    setError(null);
    try {
      setPuja(
        await api<Puja>('POST', '/sourcing/puja-maxima', {
          subasta: f.get('subasta'),
          fecha: hoy(),
          tipoCambio: num('tipoCambio'),
          trasladoUsd: num('trasladoUsd'),
          trasladoMxn: num('trasladoMxn'),
          reparacionEstimadaMxn: num('reparacion'),
          costosVentaMxn: num('venta'),
          honorariosAgenteMxn: num('honorarios'),
          valorEsperadoVentaMxn: num('valorEsperado'),
          margenObjetivoMxn: num('margen'),
          ...(String(f.get('valorReferencia') ?? '').trim() && {
            valorReferenciaMxn: String(f.get('valorReferencia')).trim(),
          }),
          margenMinimoPorcentaje: String(f.get('margenMinimo') ?? '').trim() || '20',
          tramitesUsd: num('tramites'),
          ...(String(f.get('estadoLote') ?? '').trim() && {
            estadoLote: String(f.get('estadoLote')).trim().toUpperCase(),
          }),
          exportacion: f.get('exportacion') !== null,
          ruta: f.get('ruta') ?? 'mexico',
          ...(vehiculo?.marca && { marca: vehiculo.marca }),
          ...(vehiculo?.modelo && { modelo: vehiculo.modelo }),
          ...(vehiculo?.version && { version: vehiculo.version }),
          vin: String(f.get('vin') ?? ''),
          ...(f.get('anio') && { anio: Number(f.get('anio')) }),
          titulo: f.get('titulo'),
          region: f.get('region'),
          fechaCruce: f.get('fechaCruce'),
        }),
      );
    } catch (err) {
      setError((err as Error).message);
      setPuja(null);
    } finally {
      setCalculando(false);
    }
  };

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Subastas</h1>
          <div className="subtitulo">Cuánto pujar como máximo y lo que ya compraste</div>
        </div>
      </div>
      <section className="seccion">
        <header>
          <h2>Puja máxima</h2>
          <span className="subtitulo">
            El mayor martillo que deja tu margen objetivo, con cargos, traslado e importación
          </span>
        </header>
        <form className="cuerpo formulario" onSubmit={(e) => void calcular(e)}>
          <label className="campo">
            <span>Vehículo vigilado (opcional)</span>
            <select
              value={vehiculo?.id ?? ''}
              onChange={(e) => setVehiculo(vehiculos?.find((x) => x.id === e.target.value) ?? null)}
            >
              <option value="">Otro lote (escribe el VIN)</option>
              {(vehiculos ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {nombreVehiculo(x)} · {x.vin.slice(-6)}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>VIN</span>
            <input
              name="vin"
              required
              minLength={17}
              maxLength={17}
              key={vehiculo?.id ?? 'nuevo'}
              defaultValue={vehiculo?.vin ?? ''}
            />
          </label>
          <label className="campo">
            <span>Año (si el VIN no lo dice bien)</span>
            <input
              name="anio"
              type="number"
              min={1950}
              max={2100}
              key={vehiculo?.id ?? 'nuevo'}
              defaultValue={vehiculo?.anio ?? ''}
            />
          </label>
          <label className="campo">
            <span>Título</span>
            <select
              name="titulo"
              key={vehiculo?.id ?? 'nuevo'}
              defaultValue={vehiculo?.titulo ?? 'salvage'}
            >
              <option value="limpio">Limpio</option>
              <option value="salvage">Salvage</option>
              <option value="rebuilt">Rebuilt</option>
              <option value="otro">Otro</option>
            </select>
          </label>
          <label className="campo">
            <span>Subasta</span>
            <select name="subasta" defaultValue="copart">
              {(subastas ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>Tipo de cambio (FIX de hoy)</span>
            <input name="tipoCambio" required defaultValue={fix} key={fix} inputMode="decimal" />
          </label>
          <label className="campo">
            <span>Traslado en EE. UU. (USD)</span>
            <input name="trasladoUsd" inputMode="decimal" placeholder="850" />
          </label>
          <label className="campo">
            <span>Traslado en México (MXN)</span>
            <input name="trasladoMxn" inputMode="decimal" />
          </label>
          <label className="campo">
            <span>Reparación estimada (MXN)</span>
            <input name="reparacion" inputMode="decimal" />
          </label>
          <label className="campo">
            <span>Honorarios del agente aduanal (MXN)</span>
            <input name="honorarios" inputMode="decimal" />
          </label>
          <label className="campo">
            <span>Costos de venta (MXN)</span>
            <input name="venta" inputMode="decimal" />
          </label>
          <label className="campo">
            <span>Valor esperado de venta (MXN)</span>
            <input name="valorEsperado" required inputMode="decimal" />
          </label>
          <label className="campo">
            <span>Margen objetivo (MXN)</span>
            <input name="margen" inputMode="decimal" />
          </label>
          <label className="campo">
            <span>Valor de referencia (Libro Azul o KBB, MXN)</span>
            <input
              name="valorReferencia"
              inputMode="decimal"
              placeholder="Techo del precio realista"
            />
          </label>
          <label className="campo">
            <span>Margen mínimo (%)</span>
            <input name="margenMinimo" inputMode="decimal" defaultValue="20" />
          </label>
          <label className="campo">
            <span>Trámites en EE. UU. (USD)</span>
            <input name="tramites" inputMode="decimal" placeholder="Tag, seguro, título rebuilt" />
          </label>
          <label className="campo">
            <span>Estado del lote (sales tax)</span>
            <input
              name="estadoLote"
              maxLength={2}
              placeholder="TX"
              style={{ textTransform: 'uppercase' }}
            />
          </label>
          <label className="campo">
            <span>Ruta del auto</span>
            <select name="ruta" defaultValue="mexico">
              <option value="mexico">Importar a México</option>
              <option value="eeuu">Se queda en EE. UU.</option>
              <option value="deshuesar">Solo partes</option>
            </select>
          </label>
          <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
            <input name="exportacion" type="checkbox" defaultChecked style={{ minHeight: 0 }} />
            <span>Se exporta (exención del sales tax)</span>
          </label>
          <label className="campo">
            <span>Región de destino</span>
            <select name="region" defaultValue="frontera">
              <option value="frontera">Frontera norte</option>
              <option value="resto">Resto del país</option>
            </select>
          </label>
          <label className="campo">
            <span>Fecha estimada de cruce</span>
            <input
              name="fechaCruce"
              type="date"
              required
              defaultValue={fechaHoraLocal(new Date(Date.now() + 21 * 86_400_000)).slice(0, 10)}
            />
          </label>
          {error && (
            <p className="error" style={{ gridColumn: '1 / -1' }}>
              {error}
            </p>
          )}
          <div className="acciones">
            <button className="boton primario" disabled={calculando}>
              {calculando ? 'Calculando…' : 'Calcular puja máxima'}
            </button>
          </div>
        </form>
        {puja && (
          <div style={{ borderTop: '1px solid var(--borde-suave)' }}>
            <div className="cuerpo tarjetas">
              <div className="tarjeta">
                <div className="rotulo">Puja máxima</div>
                <div className="cifra">
                  {puja.pujaMaximaUsd ? usd(puja.pujaMaximaUsd) : 'No conviene'}
                </div>
                <div className="nota">
                  {puja.motivo ?? (puja.limiteMxn ? `Tope de costo: ${mxn(puja.limiteMxn)}` : '')}
                </div>
              </div>
              {puja.pujaSugeridaUsd && (
                <div className="tarjeta">
                  <div className="rotulo">Puja sugerida</div>
                  <div className="cifra">{usd(puja.pujaSugeridaUsd)}</div>
                  <div className="nota">
                    Número no redondo para el proxy bid
                    {puja.techoMxn ? ` · techo ${mxn(puja.techoMxn)}` : ''}
                  </div>
                </div>
              )}
              {puja.desglose && (
                <>
                  <div className="tarjeta">
                    <div className="rotulo">Cargos de subasta</div>
                    <div className="cifra">
                      {usd(
                        String(puja.desglose.cargos.reduce((t, c) => t + Number(c.cantidad), 0)),
                      )}
                    </div>
                    <div className="nota">
                      {puja.desglose.cargos.map((c) => c.concepto).join(', ')}
                    </div>
                  </div>
                  <div className="tarjeta">
                    <div className="rotulo">Importación</div>
                    <div className="cifra">{mxn(puja.desglose.importacionMxn)}</div>
                    <div className="nota">
                      {puja.desglose.impuestos
                        ? `Impuestos ${mxn(puja.desglose.impuestos.totalMxn)} + honorarios`
                        : 'Sin importación'}
                      {puja.tipoImportacion ? ` · vía ${puja.tipoImportacion}` : ''}
                      {puja.desglose.aproximado ? ' · valor en aduana aproximado' : ''}
                    </div>
                  </div>
                  <div className="tarjeta">
                    <div className="rotulo">Costo total con esa puja</div>
                    <div className="cifra">{mxn(puja.desglose.totalMxn)}</div>
                  </div>
                </>
              )}
            </div>
            <ResultadoSemaforo s={puja.semaforo} />
          </div>
        )}
      </section>
      <section className="seccion">
        <header>
          <h2>Compras registradas</h2>
          <span className="subtitulo">Se registran desde la ficha de cada vehículo</span>
        </header>
        {compras && !compras.length ? (
          <p className="vacio">Aún no registras compras.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Subasta</th>
                <th>Lote</th>
                <th>VIN</th>
                <th className="num">Martillo</th>
              </tr>
            </thead>
            <tbody>
              {(compras ?? []).map((c) => (
                <tr key={c.id}>
                  <td>{fechaCorta(c.fechaCompra)}</td>
                  <td>{subastas?.find((s) => s.id === c.subasta)?.nombre ?? c.subasta}</td>
                  <td>{c.numeroLote}</td>
                  <td>
                    <Link to={`/vehiculos/${c.vehiculoId}`} className="vin">
                      {c.vin}
                    </Link>
                  </td>
                  <td className="num">
                    {c.moneda}{' '}
                    {Number(c.precioMartillo).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
